import { useState } from "react";

export default function AdminUsersTab({ 
  data, 
  formatDate, 
  onDeleteUser, 
  onUpdatePlan 
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const filteredUsers = data?.users?.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = 
      filter === "all" ||
      (filter === "premium" && user.is_premium) ||
      (filter === "free" && !user.is_premium);
    return matchesSearch && matchesFilter;
  }) || [];

  const handleDelete = async (userId) => {
    setActionLoading(userId);
    await onDeleteUser(userId);
    setShowDeleteModal(null);
    setActionLoading(null);
  };

  const handleUpdatePlan = async (userId, plan) => {
    setActionLoading(userId);
    await onUpdatePlan(userId, plan);
    setShowPlanModal(null);
    setActionLoading(null);
  };

  return (
    <>
      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-[#2D2A26] mb-2">Delete User?</h3>
            <p className="text-[#6E6A66] mb-4">
              This will permanently delete <strong>{showDeleteModal.name}</strong> ({showDeleteModal.email}) and all their data including cancelling any active Stripe subscription. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-[#E8E4DE] text-[#6E6A66] hover:bg-[#F5F2EE]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal.user_id)}
                disabled={actionLoading === showDeleteModal.user_id}
                className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                {actionLoading === showDeleteModal.user_id ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-[#2D2A26] mb-2">Update Plan</h3>
            <p className="text-[#6E6A66] mb-4">
              Change plan for <strong>{showPlanModal.name}</strong>
            </p>
            <div className="space-y-2 mb-4">
              <button
                onClick={() => handleUpdatePlan(showPlanModal.user_id, "free")}
                disabled={actionLoading}
                className={`w-full px-4 py-3 rounded-lg border text-left ${
                  !showPlanModal.is_premium ? "border-[#A87C6D] bg-[#A87C6D]/5" : "border-[#E8E4DE] hover:bg-[#F5F2EE]"
                }`}
              >
                <span className="font-medium">Open (Free)</span>
                <span className="text-sm text-[#6E6A66] block">3 check-ins per week</span>
              </button>
              <button
                onClick={() => handleUpdatePlan(showPlanModal.user_id, "premium_monthly")}
                disabled={actionLoading}
                className={`w-full px-4 py-3 rounded-lg border text-left ${
                  showPlanModal.subscription_plan === "premium_monthly" ? "border-[#A87C6D] bg-[#A87C6D]/5" : "border-[#E8E4DE] hover:bg-[#F5F2EE]"
                }`}
              >
                <span className="font-medium">Inner Circle Monthly</span>
                <span className="text-sm text-[#6E6A66] block">Unlimited check-ins</span>
              </button>
              <button
                onClick={() => handleUpdatePlan(showPlanModal.user_id, "premium_yearly")}
                disabled={actionLoading}
                className={`w-full px-4 py-3 rounded-lg border text-left ${
                  showPlanModal.subscription_plan === "premium_yearly" ? "border-[#A87C6D] bg-[#A87C6D]/5" : "border-[#E8E4DE] hover:bg-[#F5F2EE]"
                }`}
              >
                <span className="font-medium">Inner Circle Yearly</span>
                <span className="text-sm text-[#6E6A66] block">Unlimited check-ins</span>
              </button>
            </div>
            <button
              onClick={() => setShowPlanModal(null)}
              className="w-full px-4 py-2 rounded-lg border border-[#E8E4DE] text-[#6E6A66] hover:bg-[#F5F2EE]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-5 border border-[#E8E4DE]">
          <p className="text-sm text-[#6E6A66] mb-1">Total Users</p>
          <p className="text-3xl font-light text-[#2D2A26]">{data?.stats?.total_users || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#E8E4DE]">
          <p className="text-sm text-[#6E6A66] mb-1">Inner Circle</p>
          <p className="text-3xl font-light text-[#A87C6D]">{data?.stats?.premium_users || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#E8E4DE]">
          <p className="text-sm text-[#6E6A66] mb-1">Open (Free)</p>
          <p className="text-3xl font-light text-[#2D2A26]">{data?.stats?.free_users || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#E8E4DE]">
          <p className="text-sm text-[#6E6A66] mb-1">Verified</p>
          <p className="text-3xl font-light text-[#2D2A26]">{data?.stats?.verified_users || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-[#E8E4DE] p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-[#E8E4DE] focus:outline-none focus:ring-2 focus:ring-[#A87C6D]/20 focus:border-[#A87C6D]"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                filter === "all" ? "bg-[#2D2A26] text-white" : "bg-[#F5F2EE] text-[#6E6A66] hover:bg-[#E8E4DE]"
              }`}
            >
              All ({data?.stats?.total_users || 0})
            </button>
            <button
              onClick={() => setFilter("premium")}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                filter === "premium" ? "bg-[#A87C6D] text-white" : "bg-[#F5F2EE] text-[#6E6A66] hover:bg-[#E8E4DE]"
              }`}
            >
              Inner Circle ({data?.stats?.premium_users || 0})
            </button>
            <button
              onClick={() => setFilter("free")}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                filter === "free" ? "bg-[#6E6A66] text-white" : "bg-[#F5F2EE] text-[#6E6A66] hover:bg-[#E8E4DE]"
              }`}
            >
              Open ({data?.stats?.free_users || 0})
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-[#E8E4DE] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F9F7F4] border-b border-[#E8E4DE]">
                <th className="text-left px-6 py-4 text-sm font-medium text-[#6E6A66]">User</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#6E6A66]">Plan</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#6E6A66]">Status</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#6E6A66]">Check-ins</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#6E6A66]">Joined</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#6E6A66]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-[#6E6A66]">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => (
                  <tr 
                    key={user.user_id} 
                    className={`border-b border-[#E8E4DE] hover:bg-[#FDFBF7] transition-colors ${
                      index === filteredUsers.length - 1 ? "border-b-0" : ""
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-[#2D2A26]">{user.name}</p>
                        <p className="text-sm text-[#6E6A66]">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        user.is_premium ? "bg-[#A87C6D]/10 text-[#A87C6D]" : "bg-[#E8E4DE] text-[#6E6A66]"
                      }`}>
                        {user.is_premium ? "Inner Circle" : "Open"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.is_verified ? (
                        <span className="inline-flex items-center gap-1 text-sm text-green-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Verified
                        </span>
                      ) : (
                        <span className="text-sm text-[#6E6A66]">Unverified</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-[#2D2A26]">{user.check_ins_this_week} <span className="text-[#6E6A66] text-sm">this week</span></p>
                        <p className="text-sm text-[#6E6A66]">{user.total_sessions} total</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#6E6A66]">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowPlanModal(user)}
                          className="p-2 rounded-lg hover:bg-[#F5F2EE] text-[#6E6A66] hover:text-[#A87C6D] transition-colors"
                          title="Change plan"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setShowDeleteModal(user)}
                          className="p-2 rounded-lg hover:bg-red-50 text-[#6E6A66] hover:text-red-500 transition-colors"
                          title="Delete user"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-[#6E6A66]">
        Showing {filteredUsers.length} of {data?.stats?.total_users || 0} users
      </div>
    </>
  );
}
